import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../../utils/tauri';
import { useExtensionStore } from '../../extensions/store';
import { isBundledExtension, hasAllOrNothingCapabilities } from '../../extensions/bundledIds';
import { Toggle, Row } from '../settingsPrimitives';
import { iconOfManifest } from '../extensionIcons';

/** An extension card's title: its rail icon, then the name line. Shared with the API Client card. */
export const ExtensionCardTitle = ({ Icon, text }) => (
  <span className="settings-ext-title">
    {Icon && <Icon size={15} aria-hidden className="settings-ext-icon" />}
    <span>{text}</span>
  </span>
);

/* One installed extension: enable toggle, permission toggles, and — for
   third-party marketplace extensions only — uninstall.
   Shared between the Extensions overview list and the per-extension page.
   Exported for tests: the bundled-vs-third-party split below is only
   meaningful if both branches are exercised in the same harness. */
export const InstalledExtensionCard = ({ manifest, onUninstalled }) => {
  const enabled = useExtensionStore((s) => s.enabled);
  const grantedCapabilities = useExtensionStore((s) => s.grantedCapabilities);
  const setEnabled = useExtensionStore((s) => s.setEnabled);
  const setCapabilityGranted = useExtensionStore((s) => s.setCapabilityGranted);
  const unregisterExtension = useExtensionStore((s) => s.unregisterExtension);

  const [confirmUninstall, setConfirmUninstall] = useState(false);

  const isEnabled = !!enabled[manifest.id];
  const granted = grantedCapabilities[manifest.id] || [];
  // Delivery, not trust: `isBundledExtension` (compiled into the binary), NOT
  // `manifest.hostRendered`. The two sets happen to coincide today — every
  // shipped bundled extension is host-rendered as of 05.08.2026 — which makes
  // the wrong key look correct. It is not: `hostRendered` is a field in a
  // manifest, and bootstrap.js registers marketplace extensions from cached
  // bundles that bring their own. A third-party extension could set it on
  // itself and lose its Uninstall button; nothing can add itself to the
  // compiled-in list. See extensions/bundledIds.js.
  const isBundled = isBundledExtension(manifest.id);
  // Whether this extension's capabilities are offered as one unit (the switch
  // above) or as a toggle each. Shared definition — bootstrap.js and store.js
  // key the migration and the grant-on-enable off the very same predicate, so
  // the UI cannot drift from what the store actually does.
  const allOrNothingCaps = hasAllOrNothingCapabilities(manifest);

  const handleUninstall = async () => {
    unregisterExtension(manifest.id);
    setConfirmUninstall(false);
    onUninstalled?.(manifest.id);
    if (isTauri()) {
      try {
        await invoke('delete_extension_bundle', { slug: manifest.id });
      } catch (err) {
        console.warn(`[extensions] Failed to delete cached bundle for "${manifest.id}":`, err);
      }
    }
  };

  return (
    <div className="settings-section" style={{ paddingTop: 0 }}>
      <Row
        label={<ExtensionCardTitle Icon={iconOfManifest(manifest)} text={`${manifest.name} · v${manifest.version}`} />}
        desc={manifest.publisher ? `Published by ${manifest.publisher}` : undefined}
      >
        <Toggle value={isEnabled} onChange={(v) => setEnabled(manifest.id, v)} />
      </Row>

      {/* Two different questions, so two different controls.

          THIRD-PARTY (the live branch): a toggle per capability, untouched.
          Those grants exist only because an explicit consent flow asked for
          them, and per-capability granularity is the whole point of that flow.

          ALL-OR-NOTHING (bundled/auto-granted): DORMANT since 05.08.2026 — no
          bundled extension declares a capability any more, so nothing takes
          this branch and nothing renders the section at all for a built-in.
          It stays because it is the right answer if one ever does again: every
          declared capability of such an extension is required for it to
          function, so a toggle per capability was not a control but a way to
          silently break it, and those capabilities were never individually
          consented to (bundled/index.js auto-grants the whole declared set) so
          there is no per-capability consent to withdraw.

          DB Manager was that case until 05.08.2026 and is now a built-in like
          the other six: one switch, no permission section. Its reach did not
          change (three database engines, secureStorage — the OS keychain until
          26.10.2026, hidden Vault items since) and neither did
          enforcement, which for compiled-in code is now stated honestly rather
          than pretended — capabilities/enforce.js `isCapabilityGateExempt`. The
          `ui:passwordField` reasoning (EXT-02: a guest-drawn password field is
          a louder ask than a form) is unchanged and still enforced at
          sdk/hostBridge.js for the extensions that meet it — third-party
          guests. NOTE the cost, so it is a decision and not an oversight: no
          built-in discloses its access here, DB Manager included. If that
          should change it is a change for vault/ftp/terminal too, and the
          existing position is the one below — fix the reach, not the label. */}
      {manifest.permissions?.length > 0 && (
        allOrNothingCaps ? (
          <div style={{ paddingLeft: 2, paddingBottom: 8 }}>
            <Row
              label="Permissions"
              desc={`${manifest.name} requires all of these to work, so they are granted together with the switch above: ${manifest.permissions
                .map((p) => p.capability)
                .join(', ')}.`}
            />
          </div>
        ) : (
          <div style={{ paddingLeft: 2, paddingBottom: 8 }}>
            {manifest.permissions.map((perm) => (
              <Row key={perm.capability} label={perm.capability} desc={perm.reason}>
                <Toggle
                  value={granted.includes(perm.capability)}
                  onChange={(v) => setCapabilityGranted(manifest.id, perm.capability, v)}
                />
              </Row>
            ))}
          </div>
        )
      )}

      {/* No "Built-in feature" notice here any more (removed 03.08.2026, at the
          user's request — it read as noise repeated under every built-in).
          Vault, FTP, Terminal, Chat, Documents, Task Manager and — since
          05.08.2026 — DB Manager ARE built-in features packaged as extensions:
          first-party code compiled into this binary, not installables a user
          consented to. They declare no guest capabilities, so the permissions
          section above renders nothing for them. (DB Manager is the one whose
          UI is only PARTLY host React: its schema tree still runs in the guest
          iframe. `hostRendered` is the trust statement, not an iframe census —
          see its manifest.)
          The point the notice was making still stands and is worth keeping in
          mind before adding a permission control here: a grant check in front
          of host-realm code that can call `invoke` regardless is not a
          boundary. If these ever need to LOOK permissioned, fix the reach, not
          the label. */}

      {/* Bundled extensions ship compiled into the app binary, so "Uninstall"
          was a lie: it removed nothing and only set a dismissal flag that hid
          the extension for good. Enable/disable is the only control that
          corresponds to something the app can actually do, so it is the only
          one offered. (This used to add that it stopped the card contradicting
          the "Built-in feature" row above — that row was removed on
          03.08.2026, so the contradiction is gone with it; the reason to
          withhold Uninstall is unchanged and stands on its own.)
          Third-party marketplace extensions DO have removable code on disk and
          keep the full uninstall flow below unchanged. */}
      {!isBundled && (confirmUninstall ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, paddingTop: 4 }}>
          <span className="settings-row-desc">Remove {manifest.name}?</span>
          <button
            type="button"
            className="settings-action-btn"
            onClick={handleUninstall}
          >
            Confirm
          </button>
          <button type="button" className="settings-action-btn" onClick={() => setConfirmUninstall(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="settings-action-btn"
          style={{ marginTop: 14 }}
          onClick={() => setConfirmUninstall(true)}
        >
          <Trash2 size={13} />
          Uninstall
        </button>
      ))}
    </div>
  );
};
